import { CSSProperties } from "react";

interface SpanProps extends React.HTMLAttributes<HTMLSpanElement> {
  children: React.ReactNode;
  className?: string;
  id?: string;
  style?: CSSProperties;
  role?: string;
  ariaLabelledBy?: string;
}

export const Span = ({ children, ...rest }: SpanProps) => {
  return <span {...rest}>{children}</span>;
};

/**
 * A semantic span component that wraps content in a p element.
 * Extends all standard HTML span attributes while providing a consistent
 * interface for text content. Maintains accessibility and semantic structure
 * for screen readers and other assistive technologies.
 *
 * @component
 * @param {React.ReactNode} children - The text or elements to be displayed within the span
 * @param {string} [className] - Additional CSS classes to apply to the span
 * @param {string} [id] - Unique identifier for the span element
 * @param {CSSProperties} [style] - Inline styles to apply to the span
 * @param {string} [role] - ARIA role attribute for accessibility customization
 * @param {string} [ariaLabelledBy] - References the ID of an element that labels this span
 *
 * @example
 * // Basic usage
 * <span>This is a simple span of text.</span>
 *
 * @example
 * // With styling and classes
 * <span
 *   className="intro-text"
 *   style={{ fontSize: '18px', lineHeight: '1.6' }}
 * >
 *   This is an introductory span with custom styling.
 * </span>
 *
 * @example
 * // With accessibility attributes
 * <span
 *   id="description"
 *   role="note"
 *   ariaLabelledBy="section-title"
 * >
 *   This span provides additional context for the section above.
 * </span>
 *
 * @example
 * // With mixed content
 * <span className="highlight">
 *   This span contains <strong>bold text</strong> and a
 *   <a href="/link">clickable link</a>.
 * </span>
 */
